/**
 * Integrytas Group - Base App (MOBILE)
 * Page: ARL.CONFIG — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/ArlConfigPage.tsx. A DISTINCT
 * presentation (KPI tiles + segmented card lists + bottom-sheet editors) over the
 * SAME data/logic layer as the desktop page — never the desktop grid re-hosted (§16):
 *   - reads via apiDb (arl_rule / arl_endpoint_cost / arl_violation_log, plus the
 *     ARL.ENABLED system parameter), resolving each violation's user_id to a human
 *     label via csm_user_master (never a raw id — §17.B4),
 *   - every write (enforcement toggle, rule/cost add·edit·delete) routes through the
 *     Common Routine via routed_writes — §2 single write path,
 *   - "Reset buckets" calls the arl_reset RPC (clears the UNLOGGED counter table),
 *     exactly as the desktop page does.
 * Dimension / match-type CHECK-enum labels go through i18n labelled constants;
 * Y/N renders as status pills; dates via the locale-aware formatter (§11).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, Inbox, Plus, Trash2, X, RefreshCw, Gauge, Coins, ShieldAlert,
} from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
} from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ARL.CONFIG';

// ---------------------------------------------------------------------------
// Row shapes — column names verified against the desktop ArlConfigPage query.
// ---------------------------------------------------------------------------

interface RuleRow {
  arl_rule_id: number;
  internal_code: string;
  name: string;
  dimension: string;
  scope_key: string | null;
  limit_tokens: number;
  window_seconds: number;
  is_active: 'Y' | 'N';
}

interface CostRow {
  arl_endpoint_cost_id: number;
  match_type: string;
  match_value: string;
  cost: number;
  is_active: 'Y' | 'N';
}

interface ViolRow {
  arl_violation_log_id: number;
  dimension: string | null;
  endpoint: string | null;
  cost: number | null;
  ip_address: string | null;
  user_id: number | null;
  created_at: string;
}

type ActiveView = 'rules' | 'costs' | 'viols';

// Independent is_active facet for the is_active-bearing lists (rules / costs) —
// distinct from the view selector above; defaults to Active-only (§17.B12).
type ActiveFacet = 'all' | 'active' | 'inactive';

// CHECK-enum labelled constants (§17.B11) — stored value stays the code; the
// displayed label is localized via t(). Never a bare literal in the JSX.
const DIMENSIONS = [
  { value: 'GLOBAL', label: 'Global' },
  { value: 'IP', label: 'IP address' },
  { value: 'USER', label: 'User' },
  { value: 'WORKSPACE', label: 'Workspace' },
  { value: 'ROLE', label: 'Role' },
] as const;

const MATCH_TYPES = [
  { value: 'RPC', label: 'RPC' },
  { value: 'RPC_PREFIX', label: 'RPC prefix' },
  { value: 'TABLE', label: 'Table' },
  { value: 'KIND', label: 'Kind' },
] as const;

const ENABLED_PARAM = 'ARL.ENABLED';

// New-rule defaults mirror the desktop addRule().
const NEW_RULE_DEFAULTS = { dimension: 'IP', limit_tokens: 600, window_seconds: 60 } as const;

export function MobileArlConfigPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();

  const [rules, setRules] = useState<RuleRow[]>([]);
  const [costs, setCosts] = useState<CostRow[]>([]);
  const [viols, setViols] = useState<ViolRow[]>([]);
  const [userNames, setUserNames] = useState<Record<number, string>>({});
  const [enabled, setEnabled] = useState(true);

  const [view, setView] = useState<ActiveView>('rules');
  const [activeFacet, setActiveFacet] = useState<ActiveFacet>('active');
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editingRule, setEditingRule] = useState<RuleRow | 'create' | null>(null);
  const [editingCost, setEditingCost] = useState<CostRow | 'create' | null>(null);
  const [selectedViol, setSelectedViol] = useState<ViolRow | null>(null);

  const dimensionLabel = (value: string | null): string => {
    if (!value) return t('common.none', 'None');
    const def = DIMENSIONS.find((d) => d.value === value);
    return def ? t('mobile.arl.dim.' + def.value, def.label) : value;
  };
  const matchTypeLabel = (value: string): string => {
    const def = MATCH_TYPES.find((m) => m.value === value);
    return def ? t('mobile.arl.match.' + def.value, def.label) : value;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [r, c, v, p] = await Promise.all([
        apiDb
          .from('arl_rule')
          .select('arl_rule_id, internal_code, name, dimension, scope_key, limit_tokens, window_seconds, is_active')
          .order('internal_code'),
        apiDb
          .from('arl_endpoint_cost')
          .select('arl_endpoint_cost_id, match_type, match_value, cost, is_active')
          .order('match_type'),
        apiDb
          .from('arl_violation_log')
          .select('arl_violation_log_id, dimension, endpoint, cost, ip_address, user_id, created_at')
          .order('created_at', { ascending: false })
          .limit(50),
        apiDb
          .from('csm_system_parameters')
          .select('parameter_value')
          .eq('internal_code', ENABLED_PARAM)
          .maybeSingle(),
      ]);
      if (r.error) throw r.error;
      if (c.error) throw c.error;
      if (v.error) throw v.error;

      const violRows = (v.data ?? []) as ViolRow[];
      setRules((r.data ?? []) as RuleRow[]);
      setCosts((c.data ?? []) as CostRow[]);
      setViols(violRows);
      setEnabled(((p.data as { parameter_value?: string } | null)?.parameter_value ?? 'Y') === 'Y');

      // Resolve each violator's user_id to a human label (display_name || email) —
      // never surface the raw id (§17.B4).
      const userIds = Array.from(
        new Set(violRows.map((vr) => vr.user_id).filter((id): id is number => id != null)),
      );
      if (userIds.length > 0) {
        const { data: uRows } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', userIds);
        const map: Record<number, string> = {};
        for (const u of (uRows ?? []) as { user_id: number; display_name: string | null; email: string | null }[]) {
          map[u.user_id] = u.display_name || u.email || '—';
        }
        setUserNames(map);
      } else {
        setUserNames({});
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Mount only — no locale-driven labels on this screen (dates format themselves).
  useEffect(() => { void reload(); }, []);

  const toggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next); // optimistic
    const { error: e } = await routedUpdate(session, 'csm_system_parameters', 'internal_code', ENABLED_PARAM, {
      parameter_value: next ? 'Y' : 'N',
    });
    if (e) { setEnabled(!next); setError(e.message); }
  };

  const resetBuckets = async () => {
    setBusy(true);
    setError(null);
    const { error: e } = await apiDb.rpc('arl_reset', { p_rule_code: null });
    setBusy(false);
    if (e) { setError(e.message); return; }
    void reload();
  };

  const activeRules = useMemo(() => rules.filter((x) => x.is_active === 'Y').length, [rules]);

  const filteredRules = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rules.filter((x) => {
      if (activeFacet === 'active' && x.is_active !== 'Y') return false;
      if (activeFacet === 'inactive' && x.is_active !== 'N') return false;
      if (!q) return true;
      return (
        x.name.toLowerCase().includes(q) ||
        x.internal_code.toLowerCase().includes(q) ||
        dimensionLabel(x.dimension).toLowerCase().includes(q) ||
        (x.scope_key ?? '').toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules, filter, activeFacet]);

  const filteredCosts = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return costs.filter((x) => {
      if (activeFacet === 'active' && x.is_active !== 'Y') return false;
      if (activeFacet === 'inactive' && x.is_active !== 'N') return false;
      if (!q) return true;
      return (
        x.match_value.toLowerCase().includes(q) ||
        matchTypeLabel(x.match_type).toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costs, filter, activeFacet]);

  const filteredViols = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return viols;
    return viols.filter((x) =>
      (x.endpoint ?? '').toLowerCase().includes(q) ||
      (x.ip_address ?? '').toLowerCase().includes(q) ||
      dimensionLabel(x.dimension).toLowerCase().includes(q) ||
      (x.user_id != null && (userNames[x.user_id] ?? '').toLowerCase().includes(q)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viols, filter, userNames]);

  const views: { key: ActiveView; label: string }[] = [
    { key: 'rules', label: t('mobile.arl.view.rules', 'Rules') },
    { key: 'costs', label: t('mobile.arl.view.costs', 'Costs') },
    { key: 'viols', label: t('mobile.arl.view.violations', 'Violations') },
  ];

  // Independent is_active facet — only meaningful for the is_active-bearing views.
  const showActiveFacet = view === 'rules' || view === 'costs';
  const activeFacets: { key: ActiveFacet; label: string }[] = [
    { key: 'all', label: t('mobile.arl.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const searchPlaceholder =
    view === 'rules' ? t('mobile.arl.search.rules', 'Search rules')
    : view === 'costs' ? t('mobile.arl.search.costs', 'Search costs')
    : t('mobile.arl.search.violations', 'Search violations');

  const violLabel = (v: ViolRow) => v.endpoint || dimensionLabel(v.dimension);
  const violUser = (v: ViolRow) => (v.user_id == null ? '—' : (userNames[v.user_id] ?? '—'));

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.arl.title', 'Rate Limiting')}
        description={t('mobile.arl.description', 'Token-bucket rules, endpoint costs and recent 429 breaches.')}
      />

      {/* Enforcement + buckets */}
      <div className="m-fieldcard">
        <div className="m-formrow m-formrow-inline">
          <span>
            <div className="m-formrow-l">{t('mobile.arl.enforcement', 'Rate limiting')}</div>
            <div className="m-formrow-s">
              {enabled
                ? t('mobile.arl.enforcement_on', 'Enforcing limits')
                : t('mobile.arl.enforcement_off', 'Not enforcing')}
            </div>
          </span>
          {canChange && (<button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label={t('mobile.arl.enforcement', 'Rate limiting')}
            className={'m-switch ' + (enabled ? 'm-on' : 'm-off')}
            onClick={() => void toggleEnabled()}
          >
            <span className="m-knob" />
          </button>)}
        </div>
        <div className="m-formrow m-formrow-inline">
          <span>
            <div className="m-formrow-l">{t('mobile.arl.buckets', 'Token buckets')}</div>
            <div className="m-formrow-s">{t('mobile.arl.buckets_hint', 'Clear all counters now')}</div>
          </span>
          <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={() => void resetBuckets()}>
            <RefreshCw size={16} />
            {t('mobile.arl.reset', 'Reset')}
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <MobileStatStrip
        stats={[
          { value: rules.length, label: t('mobile.arl.kpi.rules', 'Rules') },
          { value: activeRules, label: t('mobile.arl.kpi.active', 'Active') },
          { value: costs.length, label: t('mobile.arl.kpi.costs', 'Costs') },
          { value: viols.length, label: t('mobile.arl.kpi.violations', '429s') },
        ]}
      />

      {/* View selector */}
      <MobileFilterChips
        chips={views.map((vw) => ({ value: vw.key, label: vw.label }))}
        isActive={(v) => view === v}
        onSelect={(v) => setView(v as ActiveView)}
      />

      {/* Active facet — independent of the view selector; filters the shown
          is_active-bearing list (rules / costs). Defaults to Active-only. */}
      {showActiveFacet && (
        <MobileFilterChips
          chips={activeFacets.map((f) => ({ value: f.key, label: f.label }))}
          isActive={(v) => activeFacet === v}
          onSelect={(v) => setActiveFacet(v as ActiveFacet)}
        />
      )}

      {/* Search */}
      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
        />
      </div>

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {/* Add control for editable views */}
      {view === 'rules' && (
        canChange && (<button type="button" className="m-btn m-btn-ghost" onClick={() => setEditingRule('create')}>
          <Plus size={16} />
          {t('mobile.arl.add_rule', 'Add rule')}
        </button>)
      )}
      {view === 'costs' && (
        canChange && (<button type="button" className="m-btn m-btn-ghost" onClick={() => setEditingCost('create')}>
          <Plus size={16} />
          {t('mobile.arl.add_cost', 'Add endpoint cost')}
        </button>)
      )}

      {/* Lists */}
      {view === 'rules' && (
        filteredRules.length === 0 && !busy ? (
          <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.arl.no_rules', 'No rules match')}</div></MobileContentCard>
        ) : (
          <MobileContentCard list>
            {filteredRules.map((r) => (
              <MobileCardRow
                key={r.arl_rule_id}
                lead={<Gauge />}
                leadTone={r.is_active === 'Y' ? 'blue' : 'slate'}
                title={r.name}
                subtitle={
                  `${dimensionLabel(r.dimension)} · ${r.limit_tokens}/${r.window_seconds}s`
                  + (r.scope_key ? ' · ' + r.scope_key : '')
                }
                trailing={
                  <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                    {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
                chevron
                onClick={canChange ? (() => setEditingRule(r)) : undefined}
              />
            ))}
          </MobileContentCard>
        )
      )}

      {view === 'costs' && (
        filteredCosts.length === 0 && !busy ? (
          <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.arl.no_costs', 'No costs match')}</div></MobileContentCard>
        ) : (
          <MobileContentCard list>
            {filteredCosts.map((c) => (
              <MobileCardRow
                key={c.arl_endpoint_cost_id}
                lead={<Coins />}
                leadTone={c.is_active === 'Y' ? 'amber' : 'slate'}
                title={c.match_value || t('mobile.arl.any_match', '(any)')}
                subtitle={`${matchTypeLabel(c.match_type)} · ${t('mobile.arl.cost_n', 'cost {{n}}', { n: c.cost })}`}
                trailing={
                  <MobileStatusPill tone={c.is_active === 'Y' ? 'ok' : 'no'}>
                    {c.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
                chevron
                onClick={canChange ? (() => setEditingCost(c)) : undefined}
              />
            ))}
          </MobileContentCard>
        )
      )}

      {view === 'viols' && (
        filteredViols.length === 0 && !busy ? (
          <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.arl.no_violations', 'No violations')}</div></MobileContentCard>
        ) : (
          <MobileContentCard list>
            {filteredViols.map((v) => (
              <MobileCardRow
                key={v.arl_violation_log_id}
                lead={<ShieldAlert />}
                leadTone="red"
                title={violLabel(v)}
                subtitle={`${formatDateTime(v.created_at)} · ${v.ip_address ?? '—'} · ${violUser(v)}`}
                trailing={v.cost != null ? <MobileStatusPill tone="info">{t('mobile.arl.cost_n', 'cost {{n}}', { n: v.cost })}</MobileStatusPill> : undefined}
                chevron
                onClick={() => setSelectedViol(v)}
              />
            ))}
          </MobileContentCard>
        )
      )}

      {/* Editors + violation detail */}
      <RuleEditSheet
        session={session}
        target={editingRule}
        dimensionLabel={dimensionLabel}
        onClose={() => setEditingRule(null)}
        onSaved={() => { setEditingRule(null); void reload(); }}
        onDeleted={(id) => { setEditingRule(null); setRules((rs) => rs.filter((x) => x.arl_rule_id !== id)); }}
      />

      <CostEditSheet
        session={session}
        target={editingCost}
        matchTypeLabel={matchTypeLabel}
        onClose={() => setEditingCost(null)}
        onSaved={() => { setEditingCost(null); void reload(); }}
        onDeleted={(id) => { setEditingCost(null); setCosts((cs) => cs.filter((x) => x.arl_endpoint_cost_id !== id)); }}
      />

      <ViolationDetailSheet
        session={session}
        viol={selectedViol}
        userLabel={violUser}
        dimensionLabel={dimensionLabel}
        onClose={() => setSelectedViol(null)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Rule editor — create or edit a single arl_rule, saved via the Common Routine.
// ---------------------------------------------------------------------------

interface RuleEditSheetProps {
  session: AuthSession;
  target: RuleRow | 'create' | null;
  dimensionLabel: (v: string | null) => string;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: (id: number) => void;
}

function RuleEditSheet({ session, target, dimensionLabel, onClose, onSaved, onDeleted }: RuleEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [dimension, setDimension] = useState<string>(NEW_RULE_DEFAULTS.dimension);
  const [scopeKey, setScopeKey] = useState('');
  const [limitTokens, setLimitTokens] = useState<number>(NEW_RULE_DEFAULTS.limit_tokens);
  const [windowSeconds, setWindowSeconds] = useState<number>(NEW_RULE_DEFAULTS.window_seconds);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCreate = target === 'create';
  const open = target !== null;

  useEffect(() => {
    if (!target) return;
    setError(null);
    if (target === 'create') {
      setName('');
      setDimension(NEW_RULE_DEFAULTS.dimension);
      setScopeKey('');
      setLimitTokens(NEW_RULE_DEFAULTS.limit_tokens);
      setWindowSeconds(NEW_RULE_DEFAULTS.window_seconds);
      setActive(false);
    } else {
      setName(target.name);
      setDimension(target.dimension);
      setScopeKey(target.scope_key ?? '');
      setLimitTokens(target.limit_tokens);
      setWindowSeconds(target.window_seconds);
      setActive(target.is_active === 'Y');
    }
  }, [target]);

  const save = async () => {
    if (!target) return;
    if (!name.trim()) { setError(t('mobile.arl.name_required', 'A rule name is required.')); return; }
    setBusy(true);
    setError(null);
    const payload = {
      name: name.trim(),
      dimension,
      scope_key: scopeKey.trim() || null,
      limit_tokens: limitTokens,
      window_seconds: windowSeconds,
      is_active: active ? 'Y' : 'N',
    };
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = isCreate
      ? await routedInsert(session, 'arl_rule', { internal_code: `ARL.RULE_${Date.now()}`, ...payload })
      : await routedUpdate(session, 'arl_rule', 'arl_rule_id', (target as RuleRow).arl_rule_id, payload);
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const remove = async () => {
    if (!target || target === 'create') return;
    setBusy(true);
    setError(null);
    const { error: e } = await routedDelete(session, 'arl_rule', 'arl_rule_id', target.arl_rule_id);
    setBusy(false);
    if (e) { setError(e.message); return; }
    onDeleted(target.arl_rule_id);
  };

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.arl.rule_editor', 'Rule')}>
        <div className="m-grabber" />
        {target && (
          <>
            <div className="m-sheet-id">
              <span className="m-av" aria-hidden><Gauge size={20} /></span>
              <span>
                <div className="m-nm">{isCreate ? t('mobile.arl.new_rule', 'New rule') : (target as RuleRow).name}</div>
                <div className="m-em">{isCreate ? dimensionLabel(dimension) : (target as RuleRow).internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-arl-rule-name">{t('mobile.arl.field.name', 'Name')}</label>
                <input
                  id="m-arl-rule-name"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-arl-rule-dim">{t('mobile.arl.field.dimension', 'Dimension')}</label>
                <select id="m-arl-rule-dim" className="m-sel" value={dimension} onChange={(e) => setDimension(e.target.value)}>
                  {DIMENSIONS.map((d) => (
                    <option key={d.value} value={d.value}>{t('mobile.arl.dim.' + d.value, d.label)}</option>
                  ))}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-arl-rule-scope">{t('mobile.arl.field.scope', 'Scope key')}</label>
                <input
                  id="m-arl-rule-scope"
                  className="m-inp"
                  value={scopeKey}
                  onChange={(e) => setScopeKey(e.target.value)}
                  placeholder={t('mobile.arl.field.scope_hint', 'blank = all')}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-arl-rule-tokens">{t('mobile.arl.field.tokens', 'Limit (tokens)')}</label>
                <input
                  id="m-arl-rule-tokens"
                  className="m-inp"
                  type="number"
                  inputMode="numeric"
                  value={limitTokens}
                  onChange={(e) => setLimitTokens(Number(e.target.value))}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-arl-rule-window">{t('mobile.arl.field.window', 'Window (seconds)')}</label>
                <input
                  id="m-arl-rule-window"
                  className="m-inp"
                  type="number"
                  inputMode="numeric"
                  value={windowSeconds}
                  onChange={(e) => setWindowSeconds(Number(e.target.value))}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.arl.field.active_hint', 'Rule is enforced')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  aria-label={t('common.boolean.active', 'Active')}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((val) => !val)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              {!isCreate && (
                <button type="button" className="m-btn m-btn-danger" disabled={busy} onClick={() => void remove()}>
                  <Trash2 size={16} />
                  {t('common.delete', 'Delete')}
                </button>
              )}
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Endpoint-cost editor — create or edit a single arl_endpoint_cost.
// ---------------------------------------------------------------------------

interface CostEditSheetProps {
  session: AuthSession;
  target: CostRow | 'create' | null;
  matchTypeLabel: (v: string) => string;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: (id: number) => void;
}

function CostEditSheet({ session, target, matchTypeLabel, onClose, onSaved, onDeleted }: CostEditSheetProps) {
  const { t } = useTranslation();
  const [matchType, setMatchType] = useState<string>('KIND');
  const [matchValue, setMatchValue] = useState('');
  const [cost, setCost] = useState<number>(1);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCreate = target === 'create';
  const open = target !== null;

  useEffect(() => {
    if (!target) return;
    setError(null);
    if (target === 'create') {
      setMatchType('KIND');
      setMatchValue('');
      setCost(1);
      setActive(true);
    } else {
      setMatchType(target.match_type);
      setMatchValue(target.match_value);
      setCost(target.cost);
      setActive(target.is_active === 'Y');
    }
  }, [target]);

  const save = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    const payload = {
      match_type: matchType,
      match_value: matchValue.trim(),
      cost,
      is_active: active ? 'Y' : 'N',
    };
    // §2: route through the Common Routine.
    const { error: e } = isCreate
      ? await routedInsert(session, 'arl_endpoint_cost', payload)
      : await routedUpdate(session, 'arl_endpoint_cost', 'arl_endpoint_cost_id', (target as CostRow).arl_endpoint_cost_id, payload);
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const remove = async () => {
    if (!target || target === 'create') return;
    setBusy(true);
    setError(null);
    const { error: e } = await routedDelete(session, 'arl_endpoint_cost', 'arl_endpoint_cost_id', target.arl_endpoint_cost_id);
    setBusy(false);
    if (e) { setError(e.message); return; }
    onDeleted(target.arl_endpoint_cost_id);
  };

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.arl.cost_editor', 'Endpoint cost')}>
        <div className="m-grabber" />
        {target && (
          <>
            <div className="m-sheet-id">
              <span className="m-av" aria-hidden><Coins size={20} /></span>
              <span>
                <div className="m-nm">
                  {isCreate
                    ? t('mobile.arl.new_cost', 'New endpoint cost')
                    : ((target as CostRow).match_value || t('mobile.arl.any_match', '(any)'))}
                </div>
                <div className="m-em">{matchTypeLabel(matchType)}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-arl-cost-type">{t('mobile.arl.field.match_type', 'Match type')}</label>
                <select id="m-arl-cost-type" className="m-sel" value={matchType} onChange={(e) => setMatchType(e.target.value)}>
                  {MATCH_TYPES.map((m) => (
                    <option key={m.value} value={m.value}>{t('mobile.arl.match.' + m.value, m.label)}</option>
                  ))}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-arl-cost-value">{t('mobile.arl.field.match_value', 'Match value')}</label>
                <input
                  id="m-arl-cost-value"
                  className="m-inp"
                  value={matchValue}
                  onChange={(e) => setMatchValue(e.target.value)}
                  placeholder={t('mobile.arl.field.match_hint', 'rpc / table / kind')}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-arl-cost-cost">{t('mobile.arl.field.cost', 'Cost')}</label>
                <input
                  id="m-arl-cost-cost"
                  className="m-inp"
                  type="number"
                  inputMode="numeric"
                  value={cost}
                  onChange={(e) => setCost(Number(e.target.value))}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.arl.field.cost_active_hint', 'Cost is applied')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  aria-label={t('common.boolean.active', 'Active')}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((val) => !val)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              {!isCreate && (
                <button type="button" className="m-btn m-btn-danger" disabled={busy} onClick={() => void remove()}>
                  <Trash2 size={16} />
                  {t('common.delete', 'Delete')}
                </button>
              )}
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Violation detail — read-only bottom sheet (429 forensic record).
// ---------------------------------------------------------------------------

interface ViolationDetailSheetProps {
  /**
   * Only for the related-children drill-down: MobileChildDetailSheet needs a
   * session to render `csm_user_master`'s registered editor. Without it the tap
   * still opens — it just silently falls back to the generic read-only view.
   */
  session: AuthSession;
  viol: ViolRow | null;
  userLabel: (v: ViolRow) => string;
  dimensionLabel: (v: string | null) => string;
  onClose: () => void;
}

function ViolationDetailSheet({ session, viol, userLabel, dimensionLabel, onClose }: ViolationDetailSheetProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const open = viol !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={onClose} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.arl.violation_detail', 'Violation detail')}>
        <div className="m-grabber" />
        {viol && (
          <>
            <div className="m-sheet-id">
              <span className="m-av" aria-hidden><ShieldAlert size={20} /></span>
              <span>
                <div className="m-nm">{viol.endpoint || dimensionLabel(viol.dimension)}</div>
                <div className="m-em">{formatDateTime(viol.created_at)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.arl.field.dimension', 'Dimension')} value={dimensionLabel(viol.dimension)} />
              <MobileDetailRow label={t('mobile.arl.field.endpoint', 'Endpoint')} value={viol.endpoint ?? '—'} />
              <MobileDetailRow label={t('mobile.arl.field.cost', 'Cost')} value={viol.cost ?? '—'} />
              <MobileDetailRow label={t('mobile.arl.field.ip', 'IP address')} value={viol.ip_address ?? '—'} />
              <MobileDetailRow label={t('mobile.arl.field.user', 'User')} value={userLabel(viol)} />
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren
                groups={[
                  { key: 'user', table: 'csm_user_master', pkColumn: 'user_id',
                    title: t('mobile.arl.related.user', 'User'),
                    items: viol.user_id == null ? [] : [{ id: viol.user_id, label: userLabel(viol) }] },
                ]}
                session={session}
              />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={onClose} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
